import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Navigate, useParams } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import {
  ChevronRight, ChevronLeft, CreditCard, Sparkles, Calendar, MapPin,
  Image as ImageIcon, Loader2, CheckCircle2, ArrowLeft, ArrowRight,
} from "lucide-react";

const STEPS = [
  { id: "advertiser", icon: Sparkles, ar: "بياناتك", en: "Your details" },
  { id: "creative", icon: ImageIcon, ar: "محتوى الإعلان", en: "Ad creative" },
  { id: "schedule", icon: Calendar, ar: "الموقع والمدة", en: "Placement & dates" },
  { id: "review", icon: CheckCircle2, ar: "المراجعة والدفع", en: "Review & pay" },
];

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function AdvertiserNewCampaign() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  const [step, setStep] = useState(0);
  const [pricing, setPricing] = useState([]);
  const [form, setForm] = useState({
    advertiser_name: "",
    advertiser_logo: "",
    advertiser_email: "",
    advertiser_phone: "",
    title: "",
    subtitle: "",
    image: "",
    link: "",
    cta_label: "اعرف المزيد",
    placement: "homepage_mid",
    start_date: today(),
    end_date: addDays(30),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiClient.get("/advertiser/pricing").then(({ data }) => setPricing(data.items || []));
  }, []);

  useEffect(() => {
    if (isEdit) {
      apiClient.get(`/advertiser/campaigns/${editId}`).then(({ data }) => {
        setForm({
          advertiser_name: data.advertiser_name || "",
          advertiser_logo: data.advertiser_logo || "",
          advertiser_email: data.advertiser_email || "",
          advertiser_phone: data.advertiser_phone || "",
          title: data.title || "",
          subtitle: data.subtitle || "",
          image: data.image || "",
          link: data.link || "",
          cta_label: data.cta_label || "اعرف المزيد",
          placement: data.placement || "homepage_mid",
          start_date: data.start_date,
          end_date: data.end_date,
        });
      });
    }
  }, [isEdit, editId]);

  const placementInfo = pricing.find(p => p.placement === form.placement);
  const computed = useMemo(() => {
    if (!placementInfo) return { days: 0, price: 0 };
    const s = new Date(form.start_date);
    const e = new Date(form.end_date);
    const days = Math.max(0, Math.floor((e - s) / 86400000) + 1);
    return { days, price: days * placementInfo.price_per_day_sar };
  }, [form.start_date, form.end_date, placementInfo]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/advertiser/campaigns/new" }} replace />;

  const setKey = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep(s => Math.max(0, s - 1));

  const canProceed = () => {
    if (step === 0) return form.advertiser_name.trim().length > 1;
    if (step === 1) return form.title && form.image && form.link;
    if (step === 2) return form.placement && form.start_date && form.end_date && computed.days >= 7;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      let cid = editId;
      if (isEdit) {
        await apiClient.patch(`/advertiser/campaigns/${editId}`, form);
      } else {
        const { data } = await apiClient.post("/advertiser/campaigns", form);
        cid = data.id;
      }
      // Proceed to Moyasar checkout (backend returns the hosted-payment URL).
      const { data: ck } = await apiClient.post(`/advertiser/campaigns/${cid}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.assign(ck.url);
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="new-campaign-page">
      <div className="mb-8">
        <button onClick={() => navigate("/advertiser/dashboard")} className="text-sm text-[#6b6b6b] hover:text-[#0a0a0a] inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3 h-3" /> {lang === "ar" ? "العودة للوحة" : "Back to dashboard"}
        </button>
        <h1 className="font-display text-4xl tracking-[-0.02em] font-medium">{isEdit ? (lang === "ar" ? "تعديل الحملة" : "Edit campaign") : (lang === "ar" ? "حملة إعلانية جديدة" : "New ad campaign")}</h1>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center justify-between mb-10">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <React.Fragment key={s.id}>
              <div className={`flex flex-col items-center ${done ? "text-[#8a6f2e]" : active ? "text-[#0a0a0a]" : "text-[#8a8a8a]"}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${active ? "border-[#c9a55c] bg-[#c9a55c]/10" : done ? "border-[#8a6f2e] bg-[#8a6f2e] text-white" : "border-[#0a0a0a]/[0.1]"}`}>
                  {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className="text-[10px] mt-2 uppercase tracking-widest">{lang === "ar" ? s.ar : s.en}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${done ? "bg-[#8a6f2e]" : "bg-[#0a0a0a]/[0.1]"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="surface rounded-2xl p-6 sm:p-8">
        {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3 mb-4">{error}</div>}

        {/* Step 0: Advertiser */}
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "بيانات المُعلِن" : "Advertiser info"}</h2>
            <p className="text-sm text-[#6b6b6b] mb-4">{lang === "ar" ? "هذه البيانات للتواصل والفوترة." : "Used for contact & billing."}</p>
            <Field label={lang === "ar" ? "اسم الشركة / العلامة التجارية" : "Company / Brand name"} required>
              <input className="input-dark w-full" value={form.advertiser_name} onChange={(e) => setKey("advertiser_name", e.target.value)} placeholder="STC, Al Rajhi Bank…" data-testid="wiz-advertiser-name" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={lang === "ar" ? "بريد التواصل" : "Contact email"}>
                <input className="input-dark w-full" type="email" value={form.advertiser_email} onChange={(e) => setKey("advertiser_email", e.target.value)} placeholder={user.email} data-testid="wiz-advertiser-email" />
              </Field>
              <Field label={lang === "ar" ? "هاتف التواصل" : "Contact phone"}>
                <input className="input-dark w-full" value={form.advertiser_phone} onChange={(e) => setKey("advertiser_phone", e.target.value)} placeholder="+966 5x xxx xxxx" data-testid="wiz-advertiser-phone" />
              </Field>
            </div>
            <Field label={lang === "ar" ? "رابط شعار الشركة (اختياري)" : "Logo URL (optional)"}>
              <input className="input-dark w-full" value={form.advertiser_logo} onChange={(e) => setKey("advertiser_logo", e.target.value)} placeholder="https://…" data-testid="wiz-advertiser-logo" />
            </Field>
          </div>
        )}

        {/* Step 1: Creative */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "محتوى الإعلان" : "Ad creative"}</h2>
            <Field label={lang === "ar" ? "العنوان الرئيسي" : "Headline"} required>
              <input className="input-dark w-full" value={form.title} onChange={(e) => setKey("title", e.target.value)} placeholder={lang === "ar" ? "5G أسرع شبكة في المملكة" : "The fastest 5G network"} data-testid="wiz-title" />
            </Field>
            <Field label={lang === "ar" ? "العنوان الفرعي" : "Subtitle"}>
              <input className="input-dark w-full" value={form.subtitle} onChange={(e) => setKey("subtitle", e.target.value)} data-testid="wiz-subtitle" />
            </Field>
            <Field label={lang === "ar" ? "رابط صورة البنر (1200×400 على الأقل)" : "Banner image URL (1200×400 min)"} required>
              <input className="input-dark w-full" value={form.image} onChange={(e) => setKey("image", e.target.value)} placeholder="https://…" data-testid="wiz-image" />
              {form.image && <img src={form.image} alt="preview" className="mt-2 w-full max-h-48 object-cover rounded-lg" />}
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={lang === "ar" ? "الرابط عند النقر" : "Click-through URL"} required>
                <input className="input-dark w-full" value={form.link} onChange={(e) => setKey("link", e.target.value)} placeholder="https://your-site.com" data-testid="wiz-link" />
              </Field>
              <Field label={lang === "ar" ? "نص زر الإجراء" : "CTA button"}>
                <input className="input-dark w-full" value={form.cta_label} onChange={(e) => setKey("cta_label", e.target.value)} data-testid="wiz-cta" />
              </Field>
            </div>
          </div>
        )}

        {/* Step 2: Schedule */}
        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "الموقع والمدة" : "Placement & dates"}</h2>
            <div>
              <div className="text-xs text-[#6b6b6b] mb-3 uppercase tracking-widest">{lang === "ar" ? "اختر موقع الإعلان" : "Pick placement"}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {pricing.map(p => (
                  <button
                    key={p.placement}
                    type="button"
                    onClick={() => setKey("placement", p.placement)}
                    className={`text-start p-4 rounded-xl border-2 transition-all ${form.placement === p.placement ? "border-[#c9a55c] bg-[#c9a55c]/5" : "border-[#0a0a0a]/[0.08] hover:border-[#c9a55c]/40"}`}
                    data-testid={`wiz-placement-${p.placement}`}
                  >
                    <div className="font-display text-base mb-1">{lang === "ar" ? p.name_ar : p.name_en}</div>
                    <div className="text-sm text-[#8a6f2e] font-medium">{formatSAR(p.price_per_day_sar)} {lang === "ar" ? "ريال/يوم" : "SAR/day"}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={lang === "ar" ? "تاريخ البداية" : "Start date"} required>
                <input className="input-dark w-full" type="date" value={form.start_date} onChange={(e) => setKey("start_date", e.target.value)} min={today()} data-testid="wiz-start" />
              </Field>
              <Field label={lang === "ar" ? "تاريخ النهاية" : "End date"} required>
                <input className="input-dark w-full" type="date" value={form.end_date} onChange={(e) => setKey("end_date", e.target.value)} min={form.start_date} data-testid="wiz-end" />
              </Field>
            </div>
            {placementInfo && (
              <div className="surface rounded-xl p-5 bg-[#c9a55c]/5 border-[#c9a55c]/30" data-testid="wiz-price-preview">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-xs text-[#6b6b6b] uppercase tracking-widest mb-1">{lang === "ar" ? "السعر الإجمالي" : "Total price"}</div>
                    <div className="text-sm text-[#3a3a3a]">{computed.days} {lang === "ar" ? "يوم" : "days"} × {formatSAR(placementInfo.price_per_day_sar)} {lang === "ar" ? "ريال/يوم" : "SAR/day"}</div>
                  </div>
                  <div className="font-display text-3xl text-[#0a0a0a]">{formatSAR(computed.price)} <span className="text-base text-[#8a8a8a]">SAR</span></div>
                </div>
                {computed.days < 7 && <div className="text-xs text-red-700 mt-2">{lang === "ar" ? "الحد الأدنى للحملة هو 7 أيام" : "Minimum duration is 7 days"}</div>}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-5">
            <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "المراجعة والدفع" : "Review & pay"}</h2>

            {form.image && (
              <div className="surface rounded-xl overflow-hidden">
                <img src={form.image} alt="" className="w-full h-40 object-cover" />
                <div className="p-4">
                  <div className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-1">{lang === "ar" ? "معاينة الإعلان" : "Ad preview"}</div>
                  <div className="font-display text-lg">{form.title}</div>
                  {form.subtitle && <div className="text-sm text-[#6b6b6b]">{form.subtitle}</div>}
                </div>
              </div>
            )}

            <dl className="surface rounded-xl p-5 space-y-2 text-sm">
              <Row k={lang === "ar" ? "المُعلِن" : "Advertiser"} v={form.advertiser_name} />
              <Row k={lang === "ar" ? "الموقع" : "Placement"} v={placementInfo ? (lang === "ar" ? placementInfo.name_ar : placementInfo.name_en) : form.placement} />
              <Row k={lang === "ar" ? "المدة" : "Duration"} v={`${form.start_date} → ${form.end_date} (${computed.days} ${lang === "ar" ? "يوم" : "days"})`} />
              <Row k={lang === "ar" ? "السعر اليومي" : "Daily rate"} v={`${formatSAR(placementInfo?.price_per_day_sar || 0)} SAR`} />
              <div className="pt-3 border-t border-[#0a0a0a]/[0.06] flex justify-between font-display text-xl">
                <span>{lang === "ar" ? "الإجمالي" : "Total"}</span>
                <span>{formatSAR(computed.price)} SAR</span>
              </div>
            </dl>

            <div className="text-xs text-[#6b6b6b]">
              {lang === "ar"
                ? "بالنقر على «ادفع»، ستذهب إلى صفحة الدفع الآمنة عبر ميسر (Moyasar). سيبدأ إعلانك بالظهور تلقائياً بعد تأكيد الدفع وتاريخ البداية."
                : "Clicking 'Pay' takes you to Moyasar's secure checkout. Your ad will go live automatically after payment confirmation and the start date."}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between gap-2 mt-8 pt-5 border-t border-[#0a0a0a]/[0.06]">
          <button onClick={prev} disabled={step === 0} className="btn-ghost text-sm disabled:opacity-30">
            <ChevronRight className="w-4 h-4 rtl:hidden" /><ChevronLeft className="w-4 h-4 ltr:hidden" /> {lang === "ar" ? "السابق" : "Back"}
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} disabled={!canProceed()} className="btn-primary text-sm disabled:opacity-50" data-testid="wiz-next">
              {lang === "ar" ? "التالي" : "Next"} <ChevronLeft className="w-4 h-4 rtl:hidden" /><ChevronRight className="w-4 h-4 ltr:hidden" />
            </button>
          ) : (
            <button onClick={submit} disabled={saving || computed.price <= 0} className="btn-primary text-sm" data-testid="wiz-checkout">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {lang === "ar" ? `ادفع ${formatSAR(computed.price)} ريال` : `Pay ${formatSAR(computed.price)} SAR`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs text-[#6b6b6b] block mb-1">{label} {required && <span className="text-red-600">*</span>}</label>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[#8a8a8a]">{k}</dt>
      <dd className="text-[#0a0a0a] font-medium text-end">{v}</dd>
    </div>
  );
}
